import { Link } from "react-router-dom";
import { Phone, MapPin, Menu, X, ChevronDown, Search, User, Facebook, Linkedin, Youtube } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useState, useEffect } from "react";
import { CartDrawer } from "@/components/shop/CartDrawer";
import { SearchDialog } from "@/components/search/SearchDialog";
import { AnimatePresence, motion } from "framer-motion";
import logoHeader from "@/assets/logo-header.svg";
import { LocationIndicator } from "@/components/location/LocationIndicator";

const productCategories = [
"Artificial Grass",
"WPC Decking",
"WPC External Cladding",
"Indoor Cladding",
"Hedges & Screens",
"Acoustic Panels",
"Hybrid Flooring",
"Garden Pebbles"];


const navItems = [
{ label: "Home", href: "/" },
{
  label: "Shop by Category",
  href: "/shop",
  children: [
  { label: "Artificial Grass", href: "/artificial-grass" },
  { label: "WPC Decking", href: "/wpc-decking" },
  { label: "WPC External Cladding", href: "/wpc-external-cladding" },
  {
    label: "Indoor Cladding",
    href: "/indoor-cladding",
    children: [
    { label: "PS Cladding", href: "/ps-cladding" },
    { label: "Acoustic Cladding", href: "/acoustic-cladding" },
    { label: "UV Marble Sheets", href: "/uv-marble-sheets" },
    { label: "Flexible Wall Cladding", href: "/flexible-wall-cladding" }]

  },
  { label: "Supreme Wet Wall Panels", href: "/pvc-wet-wall-panels" },
  { label: "Hybrid Flooring", href: "/hybrid-flooring" },
  { label: "Hedges & Screens", href: "/hedges" },
  { label: "Weed Mats", href: "/weed-mats" },
  { label: "Garden Pebbles", href: "/garden-pebbles" },
  { label: "Accessories", href: "/accessories" }]

},
{
  label: "Resources",
  href: "/resources",
  children: [
  { label: "Installation Guides", href: "/resources/guides" },
  { label: "Area Calculator", href: "/resources/calculator" }]

},
{ label: "Blog", href: "/blog" },
{ label: "Our Picks", href: "/our-picks" },
{
  label: "About Us",
  href: "/about",
  children: [
  { label: "Our Story", href: "/about" },
  { label: "Mission & Values", href: "/about#mission" },
  { label: "Our Team", href: "/about#team" },
  { label: "Careers", href: "/about#careers" }]

},
{ label: "Contact", href: "/contact" }];


export function Header() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const [searchOpen, setSearchOpen] = useState(false);

  // Keyboard shortcut for search (Cmd/Ctrl + K)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        setSearchOpen(true);
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, []);

  const [activeCategoryIndex, setActiveCategoryIndex] = useState(0);

  // Rotate product categories
  useEffect(() => {
    const interval = setInterval(() => {
      setActiveCategoryIndex((prev) => (prev + 1) % productCategories.length);
    }, 2500);
    return () => clearInterval(interval);
  }, []);

  return (
    <header className="sticky top-0 z-50 w-full">
      {/* Top Bar */}
      <div className="bg-secondary text-secondary-foreground">
        <div className="container flex items-center justify-between py-2 text-sm">
          <div className="hidden items-center gap-6 lg:flex">
            <a href="tel:0457585901" className="flex items-center gap-2 hover:text-accent transition-colors">
              <Phone className="h-4 w-4" />
              <span>Need Help? Call 0457 585 901</span>
            </a>
            {/* Social Icons */}
            <div className="flex items-center gap-3 border-l border-secondary-foreground/20 pl-5">
              <a href="https://www.facebook.com/supremeturf.au" target="_blank" rel="noopener noreferrer" aria-label="Facebook" className="text-secondary-foreground/60 hover:text-accent transition-colors"><Facebook className="h-4 w-4" /></a>
              <a href="https://www.instagram.com/supreme.turf/" target="_blank" rel="noopener noreferrer" aria-label="Instagram" className="text-secondary-foreground/60 hover:text-accent transition-colors"><svg viewBox="0 0 24 24" fill="currentColor" className="h-4 w-4"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z" /></svg></a>
              <a href="https://www.tiktok.com/@supreme.turf" target="_blank" rel="noopener noreferrer" aria-label="TikTok" className="text-secondary-foreground/60 hover:text-accent transition-colors"><svg viewBox="0 0 24 24" fill="currentColor" className="h-4 w-4"><path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-2.88 2.5 2.89 2.89 0 01-2.89-2.89 2.89 2.89 0 012.89-2.89c.28 0 .54.04.79.1V9.01a6.27 6.27 0 00-.79-.05 6.34 6.34 0 00-6.34 6.34 6.34 6.34 0 006.34 6.34 6.34 6.34 0 006.33-6.34V8.93a8.15 8.15 0 004.77 1.52V7.01a4.85 4.85 0 01-1-.32z" /></svg></a>
              <a href="https://youtube.com/@supremeturf" target="_blank" rel="noopener noreferrer" aria-label="YouTube" className="text-secondary-foreground/60 hover:text-accent transition-colors"><Youtube className="h-4 w-4" /></a>
              <a href="https://www.linkedin.com/company/supremeturf/" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn" className="text-secondary-foreground/60 hover:text-accent transition-colors"><Linkedin className="h-4 w-4" /></a>
              <a href="https://wa.me/61457585901" target="_blank" rel="noopener noreferrer" aria-label="WhatsApp" className="text-secondary-foreground/60 hover:text-accent transition-colors"><svg viewBox="0 0 24 24" fill="currentColor" className="h-4 w-4"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" /></svg></a>
            </div>
          </div>
          <div className="flex items-center gap-4">
            {/* Animated Product Pill */}
            <div className="hidden md:flex items-center overflow-hidden rounded-full bg-secondary-foreground/10 px-4 py-1 text-xs font-medium">
              <span className="mr-2 h-1.5 w-1.5 rounded-full bg-accent inline-block" />
              <AnimatePresence mode="wait">
                <motion.span
                  key={activeCategoryIndex}
                  initial={{ y: 14, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  exit={{ y: -14, opacity: 0 }}
                  transition={{ duration: 0.35 }}
                  className="inline-block">

                  {productCategories[activeCategoryIndex]}
                </motion.span>
              </AnimatePresence>
            </div>
            <Link to="/contact" className="flex items-center gap-2 text-xs md:text-sm hover:text-accent transition-colors">
              <MapPin className="h-4 w-4" />
              <span>5 Locations Across QLD • NSW • VIC • SA</span>
            </Link>
            <span className="hidden h-4 w-px bg-secondary-foreground/20 md:block" />
            <LocationIndicator />
          </div>
        </div>
      </div>

      {/* Main Navigation */}
      <nav className="bg-card shadow-brand-md">
        <div className="container">
          <div className="flex h-20 items-center justify-between">
            {/* Logo */}
            <Link to="/" className="flex-shrink-0">
              <img
                src={logoHeader}
                alt="Supreme Turf"
                className="h-[44px] md:h-[60px] w-auto object-contain" />
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex lg:items-center lg:gap-1">
              {navItems.map((item) =>
              <div
                key={item.label}
                className="relative"
                onMouseEnter={() => item.children && setActiveDropdown(item.label)}
                onMouseLeave={() => setActiveDropdown(null)}>

                  <Link
                  to={item.href}
                  className="flex items-center gap-1 px-4 py-2 text-sm font-medium text-foreground hover:text-primary transition-colors">

                    {item.label}
                    {item.children && <ChevronDown className="h-4 w-4" />}
                  </Link>

                  {/* Dropdown Menu */}
                  {item.children && activeDropdown === item.label &&
                <div className="absolute left-0 top-full z-50 w-56 animate-slide-down rounded-md border bg-card p-2 shadow-brand-lg">
                      {item.children.map((child) => {
                    const hasNested = "children" in child && Array.isArray((child as any).children);
                    return (
                      <div key={child.label} className={hasNested ? "relative group/nested" : ""}>
                            <Link
                          to={child.href}
                          className="flex items-center justify-between rounded-sm px-3 py-2 text-sm text-foreground hover:bg-muted hover:text-primary transition-colors">

                              {child.label}
                              {hasNested && <ChevronDown className="h-3 w-3 -rotate-90" />}
                            </Link>
                            {hasNested &&
                        <div className="absolute left-full top-0 z-50 w-48 rounded-md border bg-card p-2 shadow-brand-lg hidden group-hover/nested:block">
                                {((child as any).children as {label: string;href: string;}[]).map((sub) =>
                          <Link
                            key={sub.label}
                            to={sub.href}
                            className="block rounded-sm px-3 py-2 text-sm text-foreground hover:bg-muted hover:text-primary transition-colors">

                                    {sub.label}
                                  </Link>
                          )}
                              </div>
                        }
                          </div>);

                  })}
                    </div>
                }
                </div>
              )}
            </div>

            {/* Desktop Actions */}
            <div className="hidden items-center gap-3 lg:flex">
              <Button variant="ghost" size="icon" aria-label="Search products" onClick={() => setSearchOpen(true)}>
                <Search className="h-5 w-5" />
              </Button>
              <Button variant="ghost" size="icon" aria-label="Account">
                <User className="h-5 w-5" />
              </Button>
              <CartDrawer />
              <Button variant="default" size="sm" asChild>
                <Link to="/quote">Get Quote</Link>
              </Button>
            </div>

            {/* Mobile Actions */}
            <div className="flex items-center gap-2 lg:hidden">
              <Button variant="default" size="sm" asChild>
                <Link to="/quote">Get Quote</Link>
              </Button>
              <CartDrawer />
              <button
                aria-label={mobileMenuOpen ? "Close menu" : "Open menu"}
                aria-expanded={mobileMenuOpen}
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>

                {mobileMenuOpen ?
                <X className="h-6 w-6" /> :

                <Menu className="h-6 w-6" />
                }
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen &&
        <div className="border-t bg-card lg:hidden max-h-[calc(100vh-5rem)] flex flex-col">
            <div className="container py-4 overflow-y-auto flex-1">
              {navItems.map((item) =>
            <div key={item.label}>
                  <Link
                to={item.href}
                className="block py-3 text-base font-medium text-foreground hover:text-primary"
                onClick={() => !item.children && setMobileMenuOpen(false)}>

                    {item.label}
                  </Link>
                  {item.children &&
              <div className="ml-4 border-l-2 border-muted pl-4">
                      {item.children.map((child) => {
                  const hasNested = "children" in child && Array.isArray((child as any).children);
                  return (
                    <div key={child.label}>
                            <Link
                        to={child.href}
                        className="block py-2 text-sm text-muted-foreground hover:text-primary font-medium"
                        onClick={() => !hasNested && setMobileMenuOpen(false)}>

                              {child.label}
                            </Link>
                            {hasNested &&
                      <div className="ml-3 border-l border-muted pl-3">
                                {((child as any).children as {label: string;href: string;}[]).map((sub) =>
                        <Link
                          key={sub.label}
                          to={sub.href}
                          className="block py-1.5 text-xs text-muted-foreground hover:text-primary"
                          onClick={() => setMobileMenuOpen(false)}>

                                    {sub.label}
                                  </Link>
                        )}
                              </div>
                      }
                          </div>);

                })}
                    </div>
              }
                </div>
            )}
            </div>
            <div className="container py-3 border-t flex gap-2">
              <Button variant="default" className="flex-1" asChild>
                <Link to="/quote" onClick={() => setMobileMenuOpen(false)}>Get Quote</Link>
              </Button>
              <CartDrawer />
            </div>
          </div>
        }
      </nav>

      {/* Search Dialog */}
      <SearchDialog open={searchOpen} onOpenChange={setSearchOpen} />
    </header>);

}